import React from 'react';
import { ClientData } from '../types/ClientData';
import { formatCurrency } from '../utils/format';
import { useCommaInput } from '../utils/useCommaInput';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

/** Compound growth: lump sum + regular monthly contributions */
function project(balance: number, monthly: number, years: number, rate: number): number {
  if (years <= 0) return balance;
  const r = rate / 12;
  const n = years * 12;
  if (r === 0) return balance + monthly * n;
  return balance * Math.pow(1 + r, n) + monthly * ((Math.pow(1 + r, n) - 1) / r);
}

export default function Investments({ data, onUpdate }: Props) {
  const pensionTotal = (data.pensionMonthly || 0) + (data.pensionCompanyFunded || 0);
  const freedomAge = data.targetFreedomAge || 55;
  const yearsToFreedom = Math.max(0, freedomAge - (data.clientAge || 40));

  // Growth rates from Assumptions
  const pensionGrowth = data.assumptionPensionGrowth || 0.08;
  const isaGrowth = data.assumptionIsaGrowth || 0.08;

  const pensionProjected = project(data.pensionBalance, pensionTotal, yearsToFreedom, pensionGrowth);
  const isaProjected = project(data.investmentsBalance, data.isaContribution, yearsToFreedom, isaGrowth);
  const combinedProjected = pensionProjected + isaProjected;

  const annualDrawdown = yearsToFreedom > 0 ? combinedProjected * 0.04 : 0; // 4% drawdown rule

  const pensionBalanceInput = useCommaInput(data.pensionBalance, v => onUpdate({ pensionBalance: v, pensionBalanceDC: v }));
  const isaBalanceInput = useCommaInput(data.isaBalance, v => onUpdate({ isaBalance: v }));

  const pensionPct = (pensionGrowth * 100).toFixed(0);
  const isaPct = (isaGrowth * 100).toFixed(0);

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ma-navy">Investments</h2>
        <p className="text-gray-500 mt-1 text-sm">
          Current balances and contributions — projected to freedom age {freedomAge}.
        </p>
      </div>

      {/* Pension card */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-4">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-base font-semibold text-ma-navy">Pension (SIPP)</h3>
            <p className="text-gray-400 text-xs mt-0.5">Personal + employer contributions</p>
          </div>
          <div className="bg-blue-50 rounded-xl px-3 py-1.5 text-center">
            <div className="text-xs text-blue-400 mb-0.5">monthly in</div>
            <div className="text-base font-bold text-blue-600">{fmt(pensionTotal)}</div>
          </div>
        </div>

        {/* Balance input */}
        <div className="mb-4">
          <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">Current balance</label>
          <div className="flex items-center mt-1.5 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-ma-navy/20 focus-within:border-ma-navy/40">
            <span className="text-gray-400 text-sm mr-2">£</span>
            <input
              {...pensionBalanceInput}
              placeholder="0"
              className="flex-1 bg-transparent text-lg font-bold text-ma-navy outline-none"
            />
          </div>
        </div>

        {/* Contribution breakdown */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="bg-gray-50 rounded-xl px-4 py-3">
            <div className="text-xs text-gray-400 mb-1">Personal</div>
            <div className="text-base font-semibold text-gray-700">{fmt(data.pensionMonthly)}<span className="text-xs text-gray-400 font-normal">/mo</span></div>
          </div>
          <div className="bg-gray-50 rounded-xl px-4 py-3">
            <div className="text-xs text-gray-400 mb-1">Employer (company)</div>
            <div className="text-base font-semibold text-gray-700">{fmt(data.pensionCompanyFunded)}<span className="text-xs text-gray-400 font-normal">/mo</span></div>
            {data.pensionCompanyFunded > 0 && (
              <div className="text-xs text-green-500 mt-0.5">saves CT on full amount</div>
            )}
          </div>
        </div>

        {/* Projection */}
        {yearsToFreedom > 0 && (
          <div className="bg-blue-50 rounded-xl px-5 py-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-blue-500 font-medium uppercase tracking-wide mb-1">Projected at age {freedomAge}</div>
                <div className="text-2xl font-bold text-blue-700">{fmt(pensionProjected)}</div>
                <div className="text-xs text-blue-400 mt-0.5">in {yearsToFreedom} year{yearsToFreedom !== 1 ? 's' : ''} at {pensionPct}%</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ISA card */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-base font-semibold text-ma-navy">Stocks & Shares ISA</h3>
            <p className="text-gray-400 text-xs mt-0.5">Tax-free growth · tax-free withdrawals</p>
          </div>
          <div className="bg-green-50 rounded-xl px-3 py-1.5 text-center">
            <div className="text-xs text-green-400 mb-0.5">monthly in</div>
            <div className="text-base font-bold text-green-600">{fmt(data.isaContribution)}</div>
          </div>
        </div>

        {/* Balance input */}
        <div className="mb-5">
          <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">Current balance</label>
          <div className="flex items-center mt-1.5 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-ma-navy/20 focus-within:border-ma-navy/40">
            <span className="text-gray-400 text-sm mr-2">£</span>
            <input
              {...isaBalanceInput}
              placeholder="0"
              className="flex-1 bg-transparent text-lg font-bold text-ma-navy outline-none"
            />
          </div>
          {data.isaContribution === 0 && (
            <p className="text-xs text-amber-500 mt-2">⚠ No ISA contribution set — update in Assets & Debts</p>
          )}
        </div>

        {/* Projection */}
        {yearsToFreedom > 0 && (
          <div className="bg-green-50 rounded-xl px-5 py-4">
            <div className="text-xs text-green-500 font-medium uppercase tracking-wide mb-1">Projected at age {freedomAge}</div>
            <div className="text-2xl font-bold text-green-700">{fmt(isaProjected)}</div>
            <div className="text-xs text-green-400 mt-0.5">in {yearsToFreedom} year{yearsToFreedom !== 1 ? 's' : ''} at {isaPct}%</div>
          </div>
        )}
      </div>

      {/* Combined summary */}
      <div className="bg-ma-navy rounded-2xl p-6 text-white">
        <div className="grid grid-cols-2 gap-6 mb-4">
          <div>
            <div className="text-white/60 text-xs mb-1">Total invested today</div>
            <div className="text-2xl font-bold">{fmt(data.pensionBalance + data.isaBalance)}</div>
          </div>
          {yearsToFreedom > 0 && (
            <div>
              <div className="text-white/60 text-xs mb-1">Combined at age {freedomAge}</div>
              <div className="text-2xl font-bold">{fmt(combinedProjected)}</div>
            </div>
          )}
        </div>

        <div className="border-t border-white/10 pt-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-white/60 text-xs mb-0.5">Monthly going in</div>
              <div className="text-lg font-semibold">{fmt(pensionTotal + data.isaContribution)}/month</div>
            </div>
            {yearsToFreedom > 0 && annualDrawdown > 0 && (
              <div className="text-right">
                <div className="text-white/60 text-xs mb-0.5">4% drawdown at {freedomAge}</div>
                <div className="text-lg font-semibold">{fmt(annualDrawdown / 12)}/month</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
